import moment from "moment";
import { jobStatusLabel } from "./dateHelpers";

/**
 * Reading a policy's automation "parts" (OD + TP, OD, TP, PA) on the screens.
 *
 * The server keeps ONE job per policy with a status for each thing being
 * bought; the list and the view carry it as `row.parts` (see
 * RayalBrokers-backend/shared/jobPartsCore.js for the shape and the rules).
 * Pure functions only — no React, so every screen reads a part the same way.
 */

/** Insurer colours, shared by the premium split table and the part lines. */
export const COMPANY_COLORS = {
  reliance: { main: "#1565C0", soft: "#E8F1FC" },
  national: { main: "#2E7D32", soft: "#EAF6EC" },
  kshema: { main: "#6A1B9A", soft: "#F3E8F8" },
  brisk: { main: "#B54708", soft: "#FEF3E7" },
};

export const insurerColor = (company) =>
  COMPANY_COLORS[String(company || "").trim().toLowerCase()] || {
    main: "#475467",
    soft: "#F2F4F7",
  };

/**
 * Status tones. The hex values are the ones JobHistoryDialog.statusColor has
 * always used; `bg` is the matching soft fill and `chip` the MUI Chip colour.
 */
export const TONES = {
  success: { color: "#2e7d32", bg: "#edf7ed", chip: "success" },
  info: { color: "#0288d1", bg: "#e1f5fe", chip: "info" },
  muted: { color: "#667085", bg: "#F2F4F7", chip: "default" },
  retry: { color: "#ed6c02", bg: "#fff3e0", chip: "warning" },
  hold: { color: "#B54708", bg: "#FFFAEB", chip: "warning" },
  warn: { color: "#f57c00", bg: "#fff3e0", chip: "warning" },
  danger: { color: "#d32f2f", bg: "#ffebee", chip: "error" },
  stopped: { color: "#E04F16", bg: "#FEF6EE", chip: "warning" },
};

export const MOTOR_KEYS = ["motor", "od", "tp"];
const PART_ORDER = ["od", "tp", "motor", "pa"];
const SHORT_LABEL = { od: "OD", tp: "TP", motor: "OD + TP", pa: "PA" };

export const shortLabel = (key) => SHORT_LABEL[key] || String(key || "");

/** Statuses arrive from three generations of jobs; fold them to one spelling. */
const normStatus = (status) => {
  const s = String(status || "pending").trim().toLowerCase();
  if (s === "complete") return "completed";
  if (s === "error") return "failed";
  return s;
};

export const isDonePart = (part) =>
  ["completed", "completed_with_errors"].includes(normStatus(part?.status));

export const isFailedPart = (part) => normStatus(part?.status).startsWith("failed");

/** One synthetic part standing in for a row that has no real ones. */
const syntheticPart = (row) => {
  const raw = normStatus(row?.jobStatus);
  return {
    key: "job",
    synthetic: true,
    label: "",
    kind: "legacy",
    company: row?.company || "",
    status: raw === "hold" ? "pending" : raw,
    held: raw === "hold",
    stopped: false,
    attempts: row?.attempts || 0,
    maxAttempts: row?.maxAttempts || 0,
    lastError: row?.lastError || null,
    result: {},
  };
};

/**
 * The parts of a row (or of a policy fetched for the view / edit).
 * Rows with no job, `jobShape: "none"` or `parts: []` fall back to ONE
 * synthetic part built from `jobStatus`, so callers never special-case them.
 */
export const partsOf = (row) => {
  const list = Array.isArray(row?.parts) ? row.parts.filter(Boolean) : [];
  return list.length ? list : [syntheticPart(row)];
};

/** Accepts a row or an already-extracted parts array. */
const toParts = (rowOrParts) =>
  Array.isArray(rowOrParts) ? rowOrParts.filter(Boolean) : partsOf(rowOrParts);

/** The real (server-built) parts — none for a synthetic fallback. */
export const realParts = (rowOrParts) => toParts(rowOrParts).filter((p) => !p.synthetic);

export const hasRealParts = (rowOrParts) => realParts(rowOrParts).length > 0;

export const sortParts = (parts) =>
  [...(parts || [])].sort((a, b) => {
    const ia = PART_ORDER.indexOf(a.key);
    const ib = PART_ORDER.indexOf(b.key);
    return (ia === -1 ? PART_ORDER.length : ia) - (ib === -1 ? PART_ORDER.length : ib);
  });

/** "TP (National)" */
export const partName = (p) => {
  const label = p?.label || shortLabel(p?.key) || "Policy";
  return p?.company ? `${label} (${p.company})` : label;
};

/**
 * "OD, TP and PA" — or, with `withCompany`, "OD (Reliance) and TP (National)".
 */
export const joinNames = (parts, { withCompany = false, conjunction = "and" } = {}) => {
  const names = (parts || []).map((p) =>
    withCompany ? partName(p) : p?.label || shortLabel(p?.key)
  );
  if (names.length <= 1) return names.join("");
  if (!conjunction) return names.join(", ");
  return `${names.slice(0, -1).join(", ")} ${conjunction} ${names[names.length - 1]}`;
};

const looksNotBuilt = (p) =>
  p?.lastErrorCode === "E100" ||
  /not (yet )?(been )?(built|automated|implemented|available)/i.test(String(p?.lastError || ""));

const retryTimeText = (when, now) => {
  const m = moment(when);
  return m.isSame(moment(now), "day") ? m.format("hh:mm A") : m.format("D MMM, hh:mm A");
};

/**
 * How one part reads on screen.
 *
 * @returns {{label, longLabel, tone, color, bg, chipColor, hint}}
 *   `label` is the short word for a table line, `longLabel` the precise one
 *   (failed at login / after submission ...), `hint` a sentence for a tooltip.
 */
export const partStatusMeta = (part, now = Date.now()) => {
  const p = part || {};
  const status = normStatus(p.status);

  const make = (label, tone, extra = {}) => ({
    label,
    longLabel: extra.longLabel || label,
    tone,
    color: TONES[tone].color,
    bg: TONES[tone].bg,
    chipColor: TONES[tone].chip,
    hint: extra.hint || "",
  });

  if (status === "cancelled" || status === "skipped") {
    return make("Skipped", "muted", { hint: "Not needed any more — an edit dropped this part." });
  }
  if (status === "completed") return make("Completed", "success");
  if (status === "completed_with_errors") {
    return make("Completed with errors", "warn", {
      hint: "Bought, but a follow-up step (such as the document upload) had a problem.",
    });
  }
  if (status === "processing") {
    return make("In progress", "info", { hint: "The automation is working on this right now." });
  }

  if (status.startsWith("failed")) {
    if (p.stopped) {
      return looksNotBuilt(p)
        ? make("Form not built yet", "stopped", {
            longLabel: "Form not built yet",
            hint: "The automation for this insurer's form does not exist yet, so it stops here.",
          })
        : make("Stopped", "stopped", {
            hint: "The automation stopped on purpose and will not retry by itself.",
          });
    }
    return make("Failed", "danger", { longLabel: jobStatusLabel(status) });
  }

  if (status !== "pending" && status !== "hold") {
    return make(jobStatusLabel(status), "muted");
  }

  // Pending, in its several flavours.
  //
  // A part that depends on another reads as WAITING for it — also when the other one failed: nothing is wrong with
  // this part, it simply starts by itself once the other has been fixed and bought. ("Blocked by TP" in red read like
  // an error on an OD that had done nothing.) The failure is still said: in the precise label and the hint.
  if (p.blockedBy) {
    const dep = shortLabel(p.blockedBy);
    return make(`Waiting for ${dep} to finish`, "muted", {
      longLabel: `Waiting for ${dep} to finish — ${dep} failed`,
      hint: `${dep} failed, so this waits. Retry ${dep}: this starts by itself once ${dep} has been bought.`,
    });
  }
  if (p.held || status === "hold") {
    return make("On hold", "hold", { hint: "The automation is paused for this policy." });
  }
  if ((p.attempts || 0) > 0) {
    const due = p.nextRetryAt ? new Date(p.nextRetryAt).getTime() : NaN;
    if (Number.isFinite(due) && due > now) {
      return make(`Retry at ${retryTimeText(due, now)}`, "retry", {
        longLabel: "Retrying",
        hint: "A step failed; the automation tries again by itself.",
      });
    }
    return make("Retrying", "retry", { hint: "A step failed; the next attempt is due now." });
  }
  if (p.waitingFor) {
    const dep = shortLabel(p.waitingFor);
    return make(`Waiting for ${dep} to finish`, "muted", { hint: `Starts once ${dep} has been bought.` });
  }
  return make("Waiting", "muted", { hint: "Queued — the automation has not started it yet." });
};

/** "1 of 3 done"; empty for a single part (nothing to summarise). */
export const partsSummary = (rowOrParts) => {
  const list = realParts(rowOrParts).filter((p) => normStatus(p.status) !== "cancelled");
  const done = list.filter(isDonePart).length;
  return { done, total: list.length, text: list.length > 1 ? `${done} of ${list.length} done` : "" };
};

export const anyPartCompleted = (row) => realParts(row).some(isDonePart);

/**
 * Parts an operator could still get bought: failed / stopped / waiting / held.
 * Not the ones bought, dropped, running, or bought together with another part.
 */
export const unfinishedParts = (row) =>
  realParts(row).filter((p) => {
    const s = normStatus(p.status);
    return p.kind !== "withPart" && (s === "pending" || s.startsWith("failed"));
  });

/** Every motor part (OD + TP, OD, TP) is bought. False without real parts. */
export const allMotorPartsDone = (row) => {
  const motor = realParts(row).filter(
    (p) => MOTOR_KEYS.includes(p.key) && normStatus(p.status) !== "cancelled"
  );
  return motor.length > 0 && motor.every(isDonePart);
};

/**
 * The list's row colours (the "Colour rows by status" switch in the filter panel, 2026-10-27; the owner picked
 * "soft tint + colour bar on the left" from three mockups). `bg` is the row, `hover` the same row under the mouse
 * (MUI's grey hover would wipe the colour), `bar` the 4px bar on the row's left edge and the legend dot.
 */
export const ROW_TONES = {
  completed: { label: "Completed", bg: "#F1FAF4", hover: "#E3F5E9", bar: "#12B76A" },
  partial: { label: "Part done", bg: "#FFFAE5", hover: "#FFF3C4", bar: "#EAAA08" },
  failed: { label: "Failed", bg: "#FEF3F2", hover: "#FDE4E2", bar: "#F04438" },
  hold: { label: "On hold", bg: "#F5F3FF", hover: "#EBE7FF", bar: "#7A5AF8" },
};

/**
 * Which colour a list row gets: "completed" | "hold" | "partial" | "failed" | null (plain).
 *
 *   - every part bought (completed with errors counts as bought) -> completed
 *   - the policy or a part is on hold -> hold; it waits for someone (Remove hold / add money), whatever else it shows
 *   - at least one bought but not all (TP done, OD pending or failed) -> partial; that beats failed, since
 *     something WAS bought and only the rest needs work
 *   - nothing bought and a part failed or stopped -> failed
 *   - otherwise (waiting, running) -> null
 *
 * Skipped parts do not count. A row with no real parts goes by `jobStatus`.
 */
export const rowTone = (row) => {
  const parts = realParts(row).filter((p) => !["cancelled", "skipped"].includes(normStatus(p.status)));
  const status = normStatus(row?.jobStatus);
  const done = parts.filter(isDonePart).length;
  if (parts.length && done === parts.length) return "completed";
  if (status === "hold" || parts.some((p) => p.held || normStatus(p.status) === "hold")) return "hold";
  if (!parts.length) {
    if (["completed", "completed_with_errors"].includes(status)) return "completed";
    return status.startsWith("failed") ? "failed" : null;
  }
  if (done > 0) return "partial";
  return parts.some(isFailedPart) ? "failed" : null;
};

/** `key:status|key:status` — a cheap "did anything move?" comparison. */
export const partsSignature = (rowOrParts) =>
  realParts(rowOrParts)
    .map((p) => `${p.key}:${normStatus(p.status)}`)
    .join("|");

/** Bought numbers to show for a part: [["Policy No", "..."], ...]. */
export const partResultLines = (part) => {
  const r = part?.result || {};
  return [
    ["Proposal No", r.proposalNumber],
    ["Policy No", r.policyNumber],
    ["Certificate No", r.certificateNo],
  ].filter(([, v]) => v);
};

/** Which KIND of number a label names — the same value under another kind is not a repeat. */
const numberKind = (label) => {
  const l = String(label || "").toLowerCase();
  if (l.includes("policy")) return "policy";
  if (l.includes("proposal") || l.includes("referral")) return "proposal";
  return "certificate";
};

/**
 * The numbers a policy row shows on hover, each ONCE.
 *
 * The row carries the policy's own numbers (policyNumber / proposalNumber), and a
 * bundled single-company policy has ONE motor part whose result holds those very
 * same numbers — so the list used to print the policy number and the proposal
 * number twice, once as "Policy No / Proposal No" and again as "OD + TP
 * (Reliance): Proposal No … · Policy No …".
 *
 *   - one bought motor part (or none): the policy-level rows say it all; a part
 *     only adds what the policy rows do not already show (a Brisk certificate);
 *   - two or more bought motor parts (OD and TP from two insurers): the PARTS
 *     say whose number is whose, so they come first and a policy-level row only
 *     survives when no part carries that value.
 *
 * A value is a repeat only under the same kind (a policy number equal to a
 * proposal number keeps both labels).
 *
 * @returns {{policy: Array<[string,string]>, parts: Array<{part: Object, lines: Array<[string,string]>}>}}
 */
export const policyNumberRows = (row) => {
  const numbered = realParts(row)
    .filter((p) => normStatus(p.status) !== "cancelled")
    .map((part) => ({ part, lines: partResultLines(part) }))
    .filter((entry) => entry.lines.length > 0);

  const policyLines = [
    ["Policy No", row?.policyNumber],
    ["Referral / Proposal No", row?.proposalNumber],
  ].filter(([, value]) => value);

  const seen = new Set();
  const fresh = (lines) =>
    lines.filter(([label, value]) => {
      const key = `${numberKind(label)}|${String(value).trim().toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  const takeParts = () =>
    numbered
      .map(({ part, lines }) => ({ part, lines: fresh(lines) }))
      .filter((entry) => entry.lines.length > 0);

  const boughtMotorParts = numbered.filter((entry) => MOTOR_KEYS.includes(entry.part.key)).length;
  if (boughtMotorParts > 1) {
    const parts = takeParts();
    return { policy: fresh(policyLines), parts };
  }
  const policy = fresh(policyLines);
  return { policy, parts: takeParts() };
};

/**
 * What a Retry will do: which parts run again and which are kept.
 *
 * A bought part is never bought twice. The one exception is an unpaid
 * payment-link policy, whose motor parts may go through again (a bought Brisk
 * certificate never does). A part bought together with another follows it.
 */
export const retryPlan = (row, { paymentLink = false } = {}) => {
  const parts = realParts(row).filter((p) => normStatus(p.status) !== "cancelled");
  const rerun = [];
  const kept = [];
  for (const p of parts) {
    if (p.kind === "withPart") continue;
    const relaunch =
      !isDonePart(p) || (paymentLink && MOTOR_KEYS.includes(p.key) && p.kind !== "brisk");
    (relaunch ? rerun : kept).push(p);
  }
  for (const p of parts) {
    if (p.kind !== "withPart") continue;
    (rerun.some((r) => r.key === p.mirrors) ? rerun : kept).push(p);
  }
  return { rerun: sortParts(rerun), kept: sortParts(kept) };
};
